import * as React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

import katex from 'katex';
import renderA11yString from 'katex/dist/contrib/render-a11y-string';
import { unescape } from 'lodash';

import { VisuallyHidden } from '@coursera/cds-core';

import 'css!./__styles__/katex';
import 'css!katex/dist/katex.min.css';

// Match text inside $$ $$ or \( \)
const LATEX_INLINE_REGEXP = /(\$\$|\\\()((.|\n|\r)*?)(\$\$|\\\))/gm;

// Match text inside \[ \]
const LATEX_BLOCK_REGEXP = /(\\\[)((.|\n|\r)*?)(\\\])/gm;

const getA11yString = (str: string) => {
  try {
    // Some functions are not supported on KaTeX render-a11y-string. Fallback to original LaTeX
    // string on that case.
    return renderA11yString(str);
  } catch {
    return str;
  }
};

type ErrorHandler = (latex: string, error: string) => string | void;

const defaultErrorHandler = (latex: string) => {
  // Replace surrounding $$ to \( \) if Katex failed to avoid false snippet detection if there
  // are two formulas in one string.
  return `\\(${latex}\\)`;
};

type Props = { katexHtml: string; a11yString: string };

const Math = ({ katexHtml, a11yString }: Props) => {
  return (
    <React.Fragment>
      <span aria-hidden="true" role="math">
        <span
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: katexHtml }}
        />
      </span>
      <VisuallyHidden component="span">{a11yString}</VisuallyHidden>
    </React.Fragment>
  );
};

const katexReplacer =
  (onError: ErrorHandler, options = {}) =>
  (_match: string, _p1: string, latex: string): string => {
    const unescaped = unescape(latex);
    try {
      return renderToStaticMarkup(
        <Math katexHtml={katex.renderToString(unescaped, options)} a11yString={getA11yString(unescaped)} />
      );
    } catch (e) {
      return onError(latex, e.message) || defaultErrorHandler(latex);
    }
  };

/**
 * Replace LaTeX equations to HTML with KaTeX.
 * Matches equations inside $$ $$, \( \) or \[ \] for block equations.
 * If transformation is failed for some reason (unsupported or wrong syntax), it will
 * leave LaTeX code as is.
 */

export const katexify = (str: string, onError: ErrorHandler = defaultErrorHandler): string =>
  str
    .replace(LATEX_INLINE_REGEXP, katexReplacer(onError))
    .replace(LATEX_BLOCK_REGEXP, katexReplacer(onError, { displayMode: true }));

export default katexify;
